import React, { useEffect, useState } from 'react';
import { Modal, Timeline, Spin, Empty } from 'antd';
import {
  ClockCircleOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ScanOutlined,
  InboxOutlined,
  FileDoneOutlined,
  PrinterOutlined
} from '@ant-design/icons';
import { makePostCall, STUDY_DATETIME_FORMAT } from '../utils/helper';
import moment from 'moment';

interface StudyHistoryDialogProps {
  visible: boolean;
  orderId: number;
  onClose: () => void;
}

interface TimelineEvent {
  event: string;
  timestamp: string;
  icon: string;
  user: string | null;
  type: string;
  receivedBy?: string | null;
}

const iconMap: { [key: string]: React.ReactNode } = {
  order: <ClockCircleOutlined style={{ fontSize: '16px' }} />,
  scan: <ScanOutlined style={{ fontSize: '16px' }} />,
  received: <InboxOutlined style={{ fontSize: '16px' }} />,
  draft: <FileTextOutlined style={{ fontSize: '16px' }} />,
  signoff: <CheckCircleOutlined style={{ fontSize: '16px' }} />,
  print: <PrinterOutlined style={{ fontSize: '16px' }} />
};

const colorMap: { [key: string]: string } = {
  order: 'blue',
  scan: 'cyan',
  received: 'green',
  draft: 'orange',
  signoff: 'green',
  print: 'purple'
};

const StudyHistoryDialog: React.FC<StudyHistoryDialogProps> = ({
  visible,
  orderId,
  onClose
}) => {
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && orderId) {
      fetchStudyHistory();
    }
  }, [visible, orderId]);

  const fetchStudyHistory = async () => {
    setLoading(true);
    try {
      const response = await makePostCall('/get-study-history', {
        order_id: orderId
      });
      setTimeline(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching study history:', error);
      setTimeline([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Study History Timeline"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
    >
      <div style={{ maxHeight: '65vh', overflowY: 'auto', overflowX: 'hidden', padding: '0.5rem' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        ) : timeline.length === 0 ? (
          <Empty
            description="No timeline data available"
            style={{ padding: '40px 0' }}
          />
        ) : (
          <Timeline style={{ marginTop: '20px' }}>
            {timeline.map((event, index) => (
              <Timeline.Item
                key={index}
                dot={iconMap[event.icon] || <ClockCircleOutlined style={{ fontSize: '16px' }} />}
                color={colorMap[event.icon] || 'blue'}
              >
                <div style={{ marginBottom: '8px' }}>
                  <strong style={{ fontSize: '14px' }}>{event.event}</strong>
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {moment(event.timestamp).format(STUDY_DATETIME_FORMAT)}
                </div>
                {event.user && (
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    By: {event.user}
                  </div>
                )}
                {event.receivedBy && (
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    Received By: {event.receivedBy}
                  </div>
                )}
              </Timeline.Item>
            ))}
          </Timeline>
        )}
      </div>
    </Modal>
  );
};

export default StudyHistoryDialog;
